import {
  filterDefinitions,
  getFilterDefinition
} from '../catalog/filter_definitions'
import { filterItems } from '../catalog/filter_engine'
import sortDefinitions from '../catalog/sort_definitions'
import { attributeMap } from '../utils/dom'

/**
 * @class
 */
class CatalogModel {
  #items = []
  #ready = false

  get items() {
    return structuredClone(this.#items)
  }

  /**
   * Элементы разобраны. Постраничный каталог получает их отдельным запросом
   * (ClubsController#catalog_data), и до ответа считать по ним нечего.
   * @returns {boolean}
   */
  get ready() {
    return this.#ready
  }

  setItems(items) {
    this.#items = structuredClone(items)
  }

  /** Забывает элементы прошлого каталога, пока данные нового не пришли. */
  reset() {
    this.#items = []
    this.#ready = false
  }

  /**
   * Читает data-атрибуты DOM-узлов элементов каталога и формирует внутреннее представление.
   * Автоматически определяет список фильтров по `data-filter-*` атрибутам узлов.
   * @param {Element[]} nodes - DOM-узлы элементов каталога.
   */
  parseNodesIntoItems(nodes) {
    this.parseRecords(Array.from(nodes, attributeMap))
  }

  /**
   * То же для наборов атрибутов `{ 'data-id': '7', 'data-rating': '4.5', … }` — строк данных
   * каталога, у которого в разметке только одна страница карточек. Разбор общий с DOM, иначе
   * фильтр по данным разошёлся бы с фильтром по карточкам.
   * @param {Object.<string, string|null>[]} records
   */
  parseRecords(records) {
    const filterNames = this.#filterNames(records)
    const items = records.map((record, originalIndex) => {
      const filterData = [...filterNames].reduce((data, filterName) => {
        const definition = getFilterDefinition(filterName)
        const rawValue = record[definition.itemAttribute] ?? null
        data[filterName] = definition.parseItemValue(rawValue)

        return data
      }, {})

      return {
        id: String(record['data-id']),
        originalIndex,
        createdAt: this.#numberValue(record['data-created-at']),
        // Ручной бонус, виджет и заполненность карточки: ключи сортировки
        // «high-rating», не фильтры.
        sortManual: this.#numberValue(record['data-sort-manual']),
        widget: this.#numberValue(record['data-widget']),
        completeness: this.#numberValue(record['data-completeness']),
        timeZone: record['data-time-zone'] || null,
        ...filterData
      }
    })

    this.setItems(items)
    this.#ready = true
  }

  /**
   * Возвращает массив id элементов, прошедших фильтрацию.
   * @param {Object.<string, *>} filters
   * @param {object} [context={}]
   * @returns {string[]}
   */
  filter(filters, context = {}) {
    const filteredItems = filterItems(this.#items, filters, context)
    return filteredItems.map((item) => item.id)
  }

  /**
   * Возвращает количество элементов, прошедших фильтрацию.
   * @param {Object.<string, *>} filters
   * @param {object} [context={}]
   * @returns {number}
   */
  count(filters, context = {}) {
    return this.filter(filters, context).length
  }

  /**
   * Вычисляет полный снимок: отфильтрованные элементы, порядок сортировки, страницу и список id для отображения.
   * @param {{ filters: object, sort: string, page: number, pageSize: number }} state
   * @param {object} [context={}]
   * @returns {{ orderedIds: string[], pageIds: string[], page: number, pageSize: number, totalItems: number, totalPages: number }}
   */
  getSnapshot(state, context = {}) {
    const filteredItems = filterItems(this.#items, state.filters, context)
    const compareItems = sortDefinitions[state.sort] || sortDefinitions.default
    const orderedItems = [...filteredItems].sort(compareItems)
    const totalItems = orderedItems.length
    const pageSize = this.#positiveInteger(state.pageSize, totalItems || 1)
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
    const requestedPage = this.#positiveInteger(state.page, 1)
    const page = Math.min(requestedPage, totalPages)
    const pageStart = (page - 1) * pageSize
    const pageItems = orderedItems.slice(pageStart, pageStart + pageSize)

    return {
      orderedIds: orderedItems.map((item) => item.id),
      pageIds: pageItems.map((item) => item.id),
      page,
      pageSize,
      totalItems,
      totalPages
    }
  }

  #numberValue(rawValue) {
    if (
      rawValue === null ||
      typeof rawValue === 'undefined' ||
      String(rawValue).trim() === ''
    ) {
      return null
    }

    const value = Number(rawValue)

    return Number.isFinite(value) ? value : null
  }

  #filterNames(records) {
    const filterNames = new Set(
      Object.keys(filterDefinitions).filter((filterName) => {
        return filterName !== 'default'
      })
    )

    records.forEach((record) => {
      Object.keys(record).forEach((name) => {
        if (!name.startsWith('data-filter-')) return

        filterNames.add(name.replace(/^data-filter-/, ''))
      })
    })

    return filterNames
  }

  #positiveInteger(value, fallback) {
    return Number.isInteger(value) && value > 0 ? value : fallback
  }
}

export { CatalogModel }
export default new CatalogModel()
